import { isEqual } from "es-toolkit";
import type { Dispatch } from "react";

import {
  addNodeAction,
  mergeNodeConfigToMultilingual,
  monolingualizeConfig,
  moveNodeAction,
  removeNodeAction,
  reorderNodeAction,
  replaceNodeAction,
  setNodeRoleAction,
  setOwnerAction,
  transformNodeSubtree,
  updateNodeConfigAction,
  type DesignerNodeData,
  type MultilingualNodeConfig,
  type NodeConfig,
  type NodeRole,
  type SlotId,
  type StateAction,
} from "@skilitics/nautilus-core-designer-data";

import { isPlaceholderCreate } from "../shared.ts";
import type { DesignerNode, GetManifestByTypeId } from "../types/index.ts";
import type { ValidateNodeConfig, ValidateNodeRole } from "./contextualSelectors/index.ts";
import { ensureMultilingualPropertiesByTypeIdCreate } from "./ensureMultilingualPropertiesByTypeId.ts";
import {
  setOptInPropertyMultilingualCreate,
  type GetMultilingualOptInEnabled,
} from "./multilingualPropertiesSelectors/index.ts";

interface MutateOptions {
  getNodeTypeId: (id: number) => string;
  getConfigMultilingual: (id: number) => DesignerNodeData["config"];
  dispatch: Dispatch<StateAction>;
  getManifest: GetManifestByTypeId;
  validateConfig: ValidateNodeConfig;
  validateNodeRole: ValidateNodeRole;
  getMultilingualOptInEnabled: GetMultilingualOptInEnabled;
  languageCodes: {
    primary: string;
    active: string;
  };
}

export const mutateCreate = ({
  dispatch,
  getConfigMultilingual,
  getManifest,
  getNodeTypeId,
  validateConfig,
  languageCodes,
  validateNodeRole,
  getMultilingualOptInEnabled,
}: MutateOptions) => {
  const ensureMultilingualPropertiesPrimary = ensureMultilingualPropertiesByTypeIdCreate({
    getManifest,
    lang: languageCodes.primary,
  });

  const ensureMultilingualPropertiesActive = ensureMultilingualPropertiesByTypeIdCreate({
    getManifest,
    lang: languageCodes.active,
  });

  const getIsPlaceholder = isPlaceholderCreate(getManifest);

  const ensureAddableNode = (startNode: DesignerNode) =>
    // convert from DesignerNode to DesignerNodeData
    transformNodeSubtree<DesignerNode, DesignerNodeData>(startNode, (node) => ({
      ...node,
      // add is always on primary language. then you do an update config for each language
      config: ensureMultilingualPropertiesPrimary(node.typeId, node.config),
      version: getManifest(node.typeId).version,
      isPlaceholder: getIsPlaceholder(node.typeId) || undefined,
    }));

  // avoid an edge case where two updates (onConfigChange in a node designer)
  // are called one after another synchronously: the merge on subsequent is on otherwise on a stale config value
  const synchronousConfigUpdateMap = new Map<number, MultilingualNodeConfig>();

  return {
    add: (node: DesignerNode, slotId?: SlotId) => {
      const { role, typeId, id } = node;
      validateNodeRole({ slotId, role, typeId, id });
      dispatch(addNodeAction({ node: ensureAddableNode(node), slotId }));
    },
    addMultilingual: (node: DesignerNodeData, slotId?: SlotId) => dispatch(addNodeAction({ node, slotId })),
    update: (id: number, newConfig: NodeConfig) => {
      const originalMultilingualConfig = synchronousConfigUpdateMap.get(id) ?? getConfigMultilingual(id);
      const newMultilingualConfig = ensureMultilingualPropertiesActive(
        getNodeTypeId(id),
        mergeNodeConfigToMultilingual(originalMultilingualConfig, newConfig, { lang: languageCodes.active }),
      );
      // don't update if the config is the same
      if (isEqual(originalMultilingualConfig, newMultilingualConfig)) return;

      const monolingualConfig = monolingualizeConfig(newMultilingualConfig, { lang: languageCodes.active });
      validateConfig(id, monolingualConfig);

      dispatch(
        updateNodeConfigAction({
          id,
          config: newMultilingualConfig,
        }),
      );
      synchronousConfigUpdateMap.set(id, newMultilingualConfig);
    },
    replace: (replacingId: number, replacement: DesignerNode) => {
      const { role, typeId, id } = replacement;
      validateNodeRole({ replacingId, role, typeId, id });
      dispatch(
        replaceNodeAction({
          id: replacingId,
          replacement: ensureAddableNode(replacement),
        }),
      );
    },
    reorder: (id: number, newIndex: number) => dispatch(reorderNodeAction({ id, newIndex: newIndex })),
    remove: (id: number) => dispatch(removeNodeAction({ id })),
    move: (from: SlotId, to: SlotId) => dispatch(moveNodeAction({ from, to })),
    setOwner: (id: number, owner: DesignerNodeData["owner"]) => dispatch(setOwnerAction({ id, owner })),
    setRole: (id: number, role: NodeRole) => {
      validateNodeRole({ id, role });
      dispatch(setNodeRoleAction({ id, role }));
    },
    setPropertyMultilingual: setOptInPropertyMultilingualCreate({
      getManifest,
      getNodeTypeId,
      getMultilingualOptInEnabled,
      getConfigMultilingual,
      validateConfig,
      languageCodes,
      dispatch,
    }),
  };
};

export type Mutate = ReturnType<typeof mutateCreate>;
